import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { ExportToPstFormBuilderService } from '@common/components/export-to-pst/export-to-pst-form-builder.service';
import { ExportToPstHelper } from '@common/components/export-to-pst/export-to-pst.helper';
import { ExportToPSTServices } from '@common/models';
import { dateRangeValidatorFactory } from '@common/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import isEqual from 'lodash/isEqual';
import { MbsLabelSize, MbsPopupType } from 'mbs-ui-kit';
import { distinctUntilChanged } from 'rxjs/operators';
import { FormStructure, MailFormGroup } from './export-to-pst.model';

@UntilDestroy()
@Component({
  selector: 'app-export-to-PST-control',
  templateUrl: './export-to-pst.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportToPstComponent implements OnInit {
  @Input() hidden = false;
  @Input() set needResetForm(_: any) {
    if (this.formGroup) this.resetForm();
  }

  @Output() exportServices = new EventEmitter<ExportToPSTServices[]>(null);
  @Output() formValid = new EventEmitter<boolean>(true);

  public formGroup!: FormGroup<FormStructure>;
  public readonly MbsLabelSize = MbsLabelSize;
  public readonly currentDay = new Date();
  public readonly MbsPopupType = MbsPopupType;

  get mailFormGroup(): FormGroup<MailFormGroup> {
    return this.formGroup.get('mail') as FormGroup<MailFormGroup>;
  }

  get mailFilterCtrl(): FormGroup {
    return this.mailFormGroup.get('filter') as FormGroup;
  }

  get mailCheckedCtrl(): FormControl<boolean> {
    return this.formGroup.get('mail.checked') as FormControl<boolean>;
  }

  get calendarCheckedCtrl(): FormControl<boolean> {
    return this.formGroup.get('calendar.checked') as FormControl<boolean>;
  }

  get contactsCheckedCtrl(): FormControl<boolean> {
    return this.formGroup.get('contacts.checked') as FormControl<boolean>;
  }

  constructor(
    private i18nPipe: I18NextPipe,
    private formBuilderService: ExportToPstFormBuilderService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.setupFormListeners();
  }

  private initForm(): void {
    this.formGroup = this.formBuilderService.buildForm();

    this.setValidators();
    this.emitServices();
  }

  private setupFormListeners(): void {
    this.formGroup.valueChanges.pipe(distinctUntilChanged(isEqual), untilDestroyed(this)).subscribe(() => {
      this.emitServices();
    });
  }

  private setValidators(): void {
    this.mailFilterCtrl.setValidators(dateRangeValidatorFactory('from', 'to'));
    this.formGroup.setValidators(this.validateFormRequired.bind(this));
  }

  private clearValidators(): void {
    this.mailFilterCtrl.clearValidators();
    this.mailFilterCtrl.setErrors(null);

    this.formGroup.clearValidators();
    this.formGroup.setErrors(null);
  }

  private resetForm(): void {
    this.formGroup.reset(this.formBuilderService.getDefaultFormValue());
    this.setValidators();
  }

  private emitServices(): void {
    const services = ExportToPstHelper.GetServices(this.formGroup);

    this.exportServices.emit(services);
    this.formValid.emit(this.formGroup.valid);
  }

  mailFilterChange(state: boolean): void {
    state ? this.setValidators() : this.clearValidators();

    this.mailFilterCtrl.updateValueAndValidity();
    this.formGroup.updateValueAndValidity();
  }

  validateFormRequired(): ValidationErrors | null {
    if (!this.mailCheckedCtrl.value && !this.calendarCheckedCtrl.value && !this.contactsCheckedCtrl.value) {
      return {
        form: { message: this.i18nPipe.transform('exportToPST.validation.notBeEmpty', { format: 'capitalize' }) }
      };
    }

    return null;
  }
}
